import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import SearchTransfer from '../Search/SearchTransfer';
import TransferResultList from './ResultList';
import { search } from 'services/transfer';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchTransferModel } from 'ts/transfer/classes/SearchTransferModel';
import {
  Forward,
  Gross,
  TransferResultsResponse,
} from 'ts/transfer/interfaces/TransferResultsResponse';
import { useTranslation } from 'react-i18next';
import TransferResultInfo from './ResultInfo';
import SkeletonTransferResult from './TransferResultsSkeleton';
import useRecaptcha from 'hooks/useRecaptcha';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { openInNewWindow } from 'utils/utils';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

const ResultTransferPage = () => {
  const [t] = useTranslation();
  const [urlParams] = useSearchParams();
  const [response, setResponse] = useState<TransferResultsResponse>();
  const [choosenArrival, setChoosenArrival] = useState<Forward>();
  const [choosenReturn, setChoosenReturn] = useState<Forward>();
  const oneWay = urlParams.get('searchType') === '0';
  const { isRecaptchaReady, handleRecapcha } = useRecaptcha();
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const searchToken = sessionStorage.getItem('transferSearchToken') || '';
  const navigate = useNavigate();
  const idbKey = location.pathname + location.search;
  const { userSiteConfig } = useContext(MainContext);
  const stateRef = useRef<any>();
  stateRef.current = {
    response,
    choosenArrival,
    choosenReturn,
  };
  usePageInMemory(idbKey, () => saveState());
  useGetPage('Transfers/Search');

  useEffect(() => {
    if (b2b) {
      if (location.pathname !== `/transfers/results`) {
        return;
      } else {
        const latestSearch = sessionStorage.getItem('latestTransferPath');
        if (latestSearch) {
          const latest = JSON.parse(latestSearch);

          const latestSearchObj = new URLSearchParams(latest.search);
          const locationSearchObj = new URLSearchParams(location.search);

          if (latestSearchObj.toString() === locationSearchObj.toString() && !isFirstLoad.current) {
            return;
          }
        }
      }
    }
    isFirstLoad.current = false;
    (async () => {
      const pageState = await idb.getPageState(idbKey);
      if (pageState && pageState.response) {
        setResponse(pageState.response);
        setChoosenArrival(pageState.choosenArrival);
        setChoosenReturn(pageState.choosenReturn);

        idb.deletePageState(idbKey);
        return;
      }

      if (!isRecaptchaReady) return;

      const recaptchaToken = await handleRecapcha();
      if (!recaptchaToken) return;
      setResponse(undefined);
      setChoosenArrival(undefined);
      setChoosenReturn(undefined);

      const needToSearch = urlParams.get('needToSearch');
      if (needToSearch === 'false') {
        const element = document.querySelector('.search-wrapper') as HTMLElement;
        element?.classList.add('visible-responsive');
        return;
      }

      const params = new SearchTransferModel(urlParams, recaptchaToken);
      const data = await search(params);

      if (data) {
        sessionStorage.setItem('transferSearchToken', data.searchToken);
      }

      setResponse(data);
    })();
  }, [urlParams, isRecaptchaReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToDetails = () => {
    const params = new URLSearchParams();
    params.append('searchToken', searchToken);
    params.append('ukeyForward', choosenArrival?.priceKey || '');
    if (choosenReturn) {
      params.append('ukeyReturn', choosenReturn?.priceKey || '');
    }

    if (userSiteConfig.base.openDetailsInNewTab) {
      openInNewWindow(`/transfer/book?${params.toString()}`);
    } else {
      navigate({ pathname: '/transfer/book', search: params.toString() });
      saveState();
    }
  };

  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <div className="results-main transfer-search-wr">
      <div className="transfer-search">
        <SearchTransfer />
      </div>

      {!!response && (
        <Fragment>
          <div className="transfer-results">
            {!!response.forward.length && (
              <div className="transfer-results-data">
                {!oneWay && (
                  <div className="transfer-results-title mt-0">
                    {t('transfer.select_transfer')} {urlParams.get('pickup.desc')}
                  </div>
                )}

                <TransferResultList
                  data={response.forward}
                  onChoose={setChoosenArrival}
                  choosenArrivalId={choosenArrival?.priceKey}
                  choosenReturnId={choosenReturn?.priceKey}
                  currency={response.currency.toLowerCase() as keyof Gross}
                />
                {!!response.return.length && (
                  <Fragment>
                    <div className="transfer-results-title return-title">
                      {t('transfer.select_transfer')} {urlParams.get('dropoff.desc')}
                    </div>
                    <TransferResultList
                      data={response.return}
                      onChoose={setChoosenReturn}
                      choosenArrivalId={choosenArrival?.priceKey}
                      choosenReturnId={choosenReturn?.priceKey}
                      currency={response.currency.toLowerCase() as keyof Gross}
                    />
                  </Fragment>
                )}
              </div>
            )}

            {!!response.forward.length && (
              <TransferResultInfo
                mapTransfer={response.forward[0].transfer}
                choosenArrival={choosenArrival}
                choosenReturn={choosenReturn}
                oneWay={oneWay}
                currency={response.currency.toLowerCase() as keyof Gross}
                goToDetails={goToDetails}
              />
            )}
          </div>

          {!response?.forward?.length && (
            <div className="text-center w100p mt-30 text-md">{t('no_entries')}</div>
          )}
        </Fragment>
      )}
      {!response && urlParams.get('needToSearch') !== 'false' && <SkeletonTransferResult />}
    </div>
  );
};

export default withErrorBoundary(ResultTransferPage, 'ResultTransferPage');
